import React from 'react';
import { Progress, List, theme } from 'antd';
import { useTranslation } from 'react-i18next';

import { EPButton } from '@/components';
import { BucketCard, BucketHeader, BucketContent, SensorContainer } from './styled';
import { MissingDataAndImagesProps } from './StationReadinessScore.utils';

const MissingDataAndImages: React.FC<MissingDataAndImagesProps> = ({
  percentage,
  totalSensors,
  missingSensors,
  sensorData = [],
  sensorImages = [],
  onAddData,
  onAddImages,
  isLoading = false,
}) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();

  const handleAddData = (sensorId: string) => {
    if (onAddData) {
      onAddData(sensorId);
    }
  };

  const handleAddImages = (sensorId: string) => {
    if (onAddImages) {
      onAddImages(sensorId);
    }
  };

  return (
    <BucketCard>
      <BucketHeader>
        <div className="bucket-title">
          <div className="title-text">
            {t('Missing data & image')} ({missingSensors}/{totalSensors} {t('sensor')})
          </div>
        </div>
        <div className="bucket-progress">
          <div className="percentage-text">{percentage}%</div>
          <Progress
            type="circle"
            percent={percentage}
            size={20}
            strokeWidth={12}
            strokeLinecap="square"
            showInfo={false}
            strokeColor={token.colorWhite}
            trailColor={token.colorFillSecondary}
          />
        </div>
      </BucketHeader>

      <BucketContent>
        <SensorContainer>
          {sensorData.length > 0 && (
            <List
              itemLayout="vertical"
              dataSource={sensorData}
              split={false}
              renderItem={(sensor) => (
                <List.Item key={sensor.id} className="spec-item">
                  <div className="item-content">
                    <div className="text-wrapper">
                      <div className="item-title">{sensor.name}</div>
                      <div className="item-description">
                        {sensor.missingData.map((data, index) => (
                          <div key={index}>{t(data)}</div>
                        ))}
                      </div>
                    </div>
                    <div className="item-actions">
                      <EPButton
                        type="link"
                        size="small"
                        onClick={() => handleAddData(sensor.id)}
                        loading={isLoading}
                        className="action-link"
                      >
                        {t('Add data')}
                      </EPButton>
                    </div>
                  </div>
                </List.Item>
              )}
            />
          )}

          {sensorImages.length > 0 && (
            <List
              itemLayout="vertical"
              dataSource={sensorImages}
              split={false}
              renderItem={(sensor) => (
                <List.Item key={sensor.id} className="spec-item">
                  <div className="item-content">
                    <div className="text-wrapper">
                      <div className="item-title">{sensor.name}</div>
                      <div className="item-description">
                        {sensor.missingImages.map((image, index) => (
                          <div key={index}>{t(image)}</div>
                        ))}
                      </div>
                    </div>
                    <div className="item-actions">
                      <EPButton
                        type="link"
                        size="small"
                        onClick={() => handleAddImages(sensor.id)}
                        loading={isLoading}
                        className="action-link"
                      >
                        {t('Add images')}
                      </EPButton>
                    </div>
                  </div>
                </List.Item>
              )}
            />
          )}
        </SensorContainer>
      </BucketContent>
    </BucketCard>
  );
};

export default MissingDataAndImages;
